import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { useLocation, useHistory, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import debounce from 'debounce';

import { makeStyles } from '@mui/styles';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Box from '@mui/material/Box';

import { Button, Checkbox, FormControlLabel, FormGroup, IconButton, Menu } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { MoreHoriz } from '@mui/icons-material';
import colorVariables from '../../styles/variables';
import TlAutoComplete from './tl-autocomplete';
import TlEmptyResults from './tl-empty-results';
import {
  topControlsWrapper,
  pagination,
  keyButton,
  baseButton,
  smallButton,
  secondaryButton,
  smallIconButton,
  contextOverlayZIndex,
} from '../../styles/styles';
import ToggleChip from '../toggle-chip';
import useFollowing from '../../hooks/useFollowing';
import usePaginatedSearch from '../../hooks/usePaginatedSearch';
import { searchDocs as nonAdminSearchDocs } from '../../services/search-service';
import TlPagination from './tl-pagination';
import TlInitialEmptyResults from './tl-initial-empty-results';
import { sourceTypeLabels } from '../../services/content-stream-service';
import { studioPrimaryColor } from '../../styles/creative-studio/studio-variables';
import CountChip from '../count-chip';
import TlForwardBackPagination from './tl-forward-back-pagination';
import { getAdServers } from '../../services/ad-server-service';

const shadowEnd = 'rgba(205, 205, 205, 0)';
const shadowStart = 'rgba(205, 205, 205, .25)';

export const paginationComponents = {
  tlPagnation: TlPagination,
  tlForwardBack: TlForwardBackPagination,
};

/** @type {Object<string, import('@mui/styles').CSSProperties>} */
const styles = {
  listContainer: {
    width: '100%',
    margin: '0 0 8px 0',
    position: 'relative',
  },

  table: {
    borderCollapse: 'separate',
  },

  tableHeader: {
    '& th.MuiTableCell-root': {
      textAlign: 'left',
      fontSize: '11px',
      padding: '8px 14px 8px 10px',
      color: colorVariables.black,
      fontWeight: 500,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      borderTop: `1px solid ${colorVariables.grey300}`,
    },
  },

  tableRow: {
    '& td.MuiTableCell-root': {
      textAlign: 'left',
      fontSize: '14px',
      padding: '10px 14px 10px 14px',
      color: colorVariables.grey500,
      whiteSpace: 'nowrap',
    },

    '&.selected td': {
      backgroundColor: `${colorVariables.grey100} !important`,
    },

    '&:hover td': {
      cursor: 'pointer',
      backgroundColor: `${colorVariables.grey100} !important`,
    },

    '&:hover': {
      '& .MuiSvgIcon-root': {
        opacity: 1,
      },
    },
  },
  tableRowDisabled: {
    '& td.MuiTableCell-root': {
      color: colorVariables.grey400,
    },
    '&:hover td': {
      cursor: 'not-allowed',
    },
  },
  tableRowArchived: {
    '& td.MuiTableCell-root': {
      color: `${colorVariables.grey400} !important`,
    },
  },
  fieldValue: {
    whiteSpace: 'nowrap',
    fontSize: 14,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: 'flex',
  },
  filter: {
    display: 'flex',
  },
  topControlsWrapper,
  pagination,
  keyButton,
  baseButton,
  smallButton,
  smallIconButton,
  secondaryButton: {
    ...secondaryButton,
    marginLeft: 0,
  },
  filterOptions: {
    padding: '4px 20px 12px 20px',
    '& .MuiFormControlLabel-root': {
      height: '32px',
    },
    '& svg': {
      width: '22px',
      height: '22px',
    },
  },
  filterOptionLabel: {
    fontSize: '15px',
  },
  filterTitle: {
    fontSize: '15px',
    fontWeight: 600,
    borderBottom: '1px solid rgb(224, 224, 224)',
    lineHeight: '26px',
    paddingBottom: '5px',
    marginBottom: '7px',
  },
  filterButton: {
    height: '30px',
    width: '100%',
    marginTop: '10px',
    marginLeft: '0',
  },
  leftShadow: {
    position: 'absolute',
    zIndex: contextOverlayZIndex,
    right: '100%',
    top: 0,
    bottom: 0,
    width: 7,
    pointerEvents: 'none',
    background: `linear-gradient(to right, ${shadowEnd} 0%, ${shadowStart} 100%)`,
    height: '100%',
    transition: 'opacity 0.3s ease-in-out',
  },
  rightShadow: {
    position: 'absolute',
    zIndex: contextOverlayZIndex,
    left: '100%',
    top: 0,
    bottom: 0,
    width: 7,
    pointerEvents: 'none',
    background: `linear-gradient(to left, ${shadowEnd} 0%, ${shadowStart} 100%)`,
    height: '100%',
    transition: 'opacity 0.3s ease-in-out',
  },
};

const useStyles = makeStyles(styles);

/**
 *
 * @param {Object} data
 * @param {Boolean} data.renderHeader
 * @param {Object} data.objectKey
 * @param {String} data.title
 * @param {Object} data.linkTo
 * @param {Object} data.columns
 * @param {Function} data.rowClick
 * @param {Function=} data.disabled
 * @param {String} data.disabledTitle
 * @param {Function=} data.rowSelected
 * @param {Function=} data.rowArchived
 * @param {Object} data.followingFilterType
 * @param {Function} data.fetchObjectsByIds
 * @param {Function} data.fetchObjects
 * @param {Object} data.searchArgs
 * @param {Function} data.searchDocs
 * @param {Object} data.serviceArgs
 * @param {Object} data.searchType
 * @param {String} data.objectPluralLabel
 * @param {String} data.objectSingularLabel
 * @param {Boolean} data.showArchivedFilter
 * @param {Boolean} data.showSourceTypeFilter
 * @param {Boolean} data.showAdServerFilter
 * @param {Boolean=} data.showSearchBar
 * @param {String} data.actionButtonUrl
 * @param {String} data.secondaryActionButtonUrl
 * @param {Object} data.secondaryActionButtonHandler
 * @param {String} data.secondaryActionButtonLabel
 * @param {Object} data.secondaryActionButtonIcon
 * @param {Boolean} data.triggerRefresh
 * @param {Object} data.emptyStateType
 * @param {React.MouseEventHandler<HTMLButtonElement>} data.emptyStateButtonFunction
 * @param {String=} data.emptyStateButtonText
 * @param {String=} data.emptyStateLink
 * @param {String} data.accountId
 * @param {Object} data.listActionsMenuClick
 * @param {Object} data.objectFormatter
 * @param {Object} data.topControlsWrapperStyle
 * @param {Object} data.listContainerComponent
 * @param {Object} data.listContainerElevation
 * @param {Object} data.listContainerStyle
 * @param {Object} data.paginationComponent
 * @param {Object} data.paginationStyle
 * @returns
 */
const TlFilterList = ({
  renderHeader,
  objectKey,
  title,
  linkTo,
  columns,
  rowClick,
  disabled = () => false,
  disabledTitle,
  rowSelected = () => false,
  rowArchived = () => false,
  followingFilterType,
  fetchObjectsByIds,
  fetchObjects,
  searchArgs,
  searchDocs = nonAdminSearchDocs,
  serviceArgs,
  searchType,
  objectPluralLabel,
  objectSingularLabel,
  showArchivedFilter,
  showSourceTypeFilter,
  showAdServerFilter,
  showSearchBar = true,
  actionButtonUrl,
  secondaryActionButtonUrl,
  secondaryActionButtonHandler,
  secondaryActionButtonLabel,
  secondaryActionButtonIcon,
  triggerRefresh,
  emptyStateType,
  emptyStateButtonFunction,
  emptyStateButtonText = null,
  emptyStateLink,
  accountId,
  listActionsMenuClick,
  objectFormatter,
  topControlsWrapperStyle,
  listContainerComponent,
  listContainerElevation,
  listContainerStyle,
  paginationComponent,
  paginationStyle,
}) => {
  const classes = useStyles();
  const { search } = useLocation();
  const history = useHistory();

  const [showArchived, setShowArchived] = useState(false);
  const [objectsLoaded, setObjectsLoaded] = useState(false);
  const [initialLoadCount, setInitialLoadCount] = useState();
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [adServerFilterAnchor, setAdServerFilterAnchor] = useState(null);
  const [internalRefresh, setInternalRefresh] = useState(null);

  const params = new URLSearchParams(search);
  const sourceTypes = params.getAll('sourceType');
  const [filterTypes, setFilterTypes] = useState({});
  const [adServerOptions, setAdServerOptions] = useState([]);
  const [adServerOptionFilters, setAdServerOptionFilters] = useState({});

  const [rightShadowOpacity, setRightShadowOpacity] = useState(0);
  const [leftShadowOpacity, setLeftShadowOpacity] = useState(0);
  const tableRef = useRef(null);

  const PaginationComponent = paginationComponent;

  const followType = followingFilterType;
  const { followsLoading, followingFiltered, setFollowingFiltered } = useFollowing({
    followType,
    nestingParams: {
      accountId,
      sourceType: sourceTypes,
    },
  });

  const {
    totalDocs,
    links: paginationLinks,
    objects,
    handleChange,
    onSearchChange,
    onSearchClear,
    autoCompleteTerms,
    setAutoCompleteTerms,
    refreshObjects,
    loading,
  } = usePaginatedSearch({
    serviceArgs,
    fetchObjectsByIds,
    fetchObjects,
    searchDocs,
    searchArgs,
    searchType,
    followsLoading,
    followingFiltered,
    setFollowingFiltered,
    showArchived,
    sourceTypes,
  });

  const formattedObjects = (objectFormatter && objectFormatter(objects)) || objects;

  const handleFilterChange = (e, key) => {
    const {
      target: { checked },
    } = e;

    const filterTypesCopy = {
      ...filterTypes,
      [key]: checked,
    };

    setFilterTypes(filterTypesCopy);
  };

  const handleAdServerFilterChange = (e, key) => {
    const {
      target: { checked },
    } = e;

    const adServerFilterTypesCopy = {
      ...adServerOptionFilters,
      [key]: checked,
    };

    setAdServerOptionFilters(adServerFilterTypesCopy);
  };

  const applyFilters = () => {
    const filterParams = new URLSearchParams(search);
    const queryKeys = ['adServer', 'sourceType'];
    for (const k of queryKeys) {
      filterParams.delete(k);
    }

    const [adServerKey, sourceTypeKey] = queryKeys;
    for (const key of Object.keys(filterTypes)) {
      if (filterTypes[key] === true) {
        filterParams.append(sourceTypeKey, key);
      }
    }

    for (const key of Object.keys(adServerOptionFilters)) {
      if (adServerOptionFilters[key] === true) {
        filterParams.append(adServerKey, key);
      }
    }
    history.push({ search: `?${decodeURI(filterParams.toString())}` });
  };

  const renderCells = (object) => {
    return Object.values(columns).map((column) => {
      const {
        field,
        fieldFormatter,
        metric,
        links,
        default: defaultValue,
        style,
        cellOnClick,
        className,
        cellJsx,
        leftShadow,
        rightShadow,
        titleAttribute,
      } = column;

      const rowDisabled = disabled(object);

      const c = field || column;

      let fieldValue;
      if (Array.isArray(c)) {
        fieldValue = c.reduce((acc, cur) => {
          if (acc) {
            const newValue = acc[cur];
            return newValue;
          }

          return acc;
        }, object);
      } else {
        fieldValue = object[c];
      }

      if (metric && object[c]) {
        fieldValue = fieldValue[metric];
      }

      if (typeof fieldValue === 'undefined' || fieldValue === null || fieldValue === false) {
        fieldValue = defaultValue;
      }

      if (fieldFormatter) fieldValue = fieldFormatter(fieldValue);

      const keyPrefix = typeof column === 'string' ? column : field || 'links';
      const key = objectKey ? object[objectKey] : object._id;
      const cellTitle = rowDisabled ? disabledTitle : fieldValue;
      const titleValue = titleAttribute === false ? undefined : cellTitle;
      const isNameCell = cellTitle === object.name;

      return (
        <TableCell key={`${keyPrefix}-${key}`} className={className} style={style} onClick={cellOnClick}>
          {(links || []).map((l) => {
            return l(object, key);
          })}
          <div title={titleValue} className={classes.fieldValue}>
            {cellJsx ? cellJsx(object) : fieldValue}
            {isNameCell && rowArchived(object) && (
              <span
                style={{
                  backgroundColor: colorVariables.grey200,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  color: colorVariables.grey500,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                }}
              >
                Archived
              </span>
            )}
          </div>

          {rightShadow && <div className={classes.rightShadow} style={{ opacity: rightShadowOpacity }} />}
          {leftShadow && <div className={classes.leftShadow} style={{ opacity: leftShadowOpacity }} />}
        </TableCell>
      );
    });
  };

  const renderHeaderCells = () => {
    return Object.entries(columns).map(([key, value]) => {
      const { field, disableSort, style, className, sortFieldName, filterValues, headerJsx, leftShadow, rightShadow } =
        value;

      if (headerJsx) {
        return headerJsx();
      }

      const v = key === 'title' ? title : key;

      const fieldName = sortFieldName || field || value;
      if (disableSort) {
        return (
          <TableCell className={className} style={style} key={v} align="left">
            {v}
            {rightShadow && <div className={classes.rightShadow} style={{ opacity: rightShadowOpacity }} />}
            {leftShadow && <div className={classes.leftShadow} style={{ opacity: leftShadowOpacity }} />}
          </TableCell>
        );
      }

      const sort = params.get('sort');
      const [sortField, direction] = sort ? sort.split('~') : [];

      return (
        <TableCell className={className} style={style} key={v} align="left">
          <Box className={filterValues && classes.filter}>
            <TableSortLabel
              active={sortField === fieldName}
              direction={direction === 'asc' ? direction : 'desc'}
              onClick={() => {
                const newDirection = sortField && direction !== 'desc' ? 'desc' : 'asc';
                params.set('sort', `${fieldName}~${newDirection}`);
                history.push({ search: `?${decodeURI(params.toString())}` });
              }}
            >
              {v}
            </TableSortLabel>
          </Box>
          {rightShadow && <div className={classes.rightShadow} style={{ opacity: rightShadowOpacity }} />}
          {leftShadow && <div className={classes.leftShadow} style={{ opacity: leftShadowOpacity }} />}
        </TableCell>
      );
    });
  };

  const getTableRowClick = (object, rowDisabled) => {
    if (rowDisabled) return null;

    return rowClick ? (e) => rowClick(e, object) : (e) => linkTo(e, object);
  };

  const setShadowOpacity = (element) => {
    const { offsetWidth, scrollWidth, scrollLeft } = element;
    const rightOpacity = scrollLeft > 0 ? 1 : 0;
    setRightShadowOpacity(rightOpacity);
    const rightEdge = scrollWidth - scrollLeft;
    const margin = 3;
    const leftOpacity = rightEdge - margin > offsetWidth ? 1 : 0;
    setLeftShadowOpacity(leftOpacity);
  };

  const debouncedScroll = debounce((event) => {
    setShadowOpacity(event.target);
  }, 20);

  useEffect(() => {
    if (triggerRefresh || internalRefresh) {
      refreshObjects();
    }
    if (internalRefresh) {
      setInternalRefresh(false);
    }
  }, [triggerRefresh, internalRefresh, refreshObjects]);

  useEffect(() => {
    const typeParams = new URLSearchParams(search).getAll('sourceType');
    const adServerParams = new URLSearchParams(search).getAll('adServer');
    const sourceTypeFilters = {};
    const adServerFilters = {};
    for (const t of typeParams) {
      sourceTypeFilters[t] = true;
    }
    for (const a of adServerParams) {
      adServerFilters[a] = true;
    }
    setFilterTypes(sourceTypeFilters);
    setAdServerOptionFilters(adServerFilters);
  }, [search]);

  useEffect(() => {
    if (showAdServerFilter) {
      getAdServers().then(({ adServers = [] }) => {
        setAdServerOptions(adServers);
      });
    }
  }, [showAdServerFilter]);

  useEffect(() => {
    if (loading === false) {
      if (objectsLoaded === false && !followingFiltered) {
        setInitialLoadCount(formattedObjects.length);
      }
      setObjectsLoaded(true);
    }
  }, [loading]);

  useEffect(() => {
    if (objectsLoaded) {
      refreshObjects();
    }
  }, [search]);

  useEffect(() => {
    let current;
    if (tableRef.current) {
      ({ current } = tableRef);
      current.addEventListener('scroll', debouncedScroll);
      setShadowOpacity(current);
    }

    return () => {
      if (current) {
        current.removeEventListener('scroll', debouncedScroll);
      }
    };
  }, [tableRef.current, debouncedScroll]);

  const filterCount = Object.values(filterTypes).filter((v) => v === true).length;
  const adServerOptionsFilterCount = Object.values(adServerOptionFilters).filter((v) => v === true).length;

  const initialEmptyState =
    emptyStateType &&
    emptyStateButtonFunction &&
    objectsLoaded &&
    initialLoadCount === 0 &&
    formattedObjects.length === 0 &&
    !followingFiltered;

  if (objectsLoaded === false) {
    return null;
  }

  if (initialEmptyState) {
    return (
      <TlInitialEmptyResults
        showEmptyState={initialEmptyState}
        messageType={emptyStateType}
        buttonFunction={emptyStateButtonFunction}
        buttonText={emptyStateButtonText}
        knowledgeBaseUrl={emptyStateLink}
      />
    );
  }

  return (
    <div>
      <Box className={topControlsWrapperStyle || classes.topControlsWrapper}>
        {showSearchBar && (
          <TlAutoComplete
            value={autoCompleteTerms}
            onChange={onSearchChange}
            onClear={onSearchClear}
            placeholder={`Search ${objectPluralLabel || ''}`}
          />
        )}
        {showSourceTypeFilter && (
          <CountChip
            label="Source Type"
            count={filterCount}
            onClick={(e) => {
              setFilterAnchor(e.currentTarget);
            }}
          />
        )}
        {showAdServerFilter && (
          <CountChip
            label="Ad Server"
            count={adServerOptionsFilterCount}
            onClick={(e) => {
              setAdServerFilterAnchor(e.currentTarget);
            }}
          />
        )}
        {showArchivedFilter && <ToggleChip active={showArchived} setActive={setShowArchived} label="Archived" />}
        {followingFilterType && (
          <ToggleChip
            isDisabled={!!autoCompleteTerms && !followingFiltered}
            active={followingFiltered}
            setActive={setFollowingFiltered}
            label="Favorites"
          />
        )}
        <div style={{ flex: '1' }} />
        {(secondaryActionButtonUrl || secondaryActionButtonHandler) && (
          <Button
            component={secondaryActionButtonUrl && Link}
            className={classnames(classes.baseButton, classes.secondaryButton)}
            startIcon={secondaryActionButtonIcon}
            to={secondaryActionButtonUrl || undefined}
            onClick={(!secondaryActionButtonUrl && secondaryActionButtonHandler) || undefined}
          >
            {secondaryActionButtonLabel}
          </Button>
        )}
        {actionButtonUrl && (
          <Button
            component={Link}
            className={classnames(classes.baseButton, classes.keyButton, classes.smallButton)}
            startIcon={<AddIcon />}
            to={actionButtonUrl}
          >
            New {objectSingularLabel}
          </Button>
        )}
        {listActionsMenuClick && (
          <IconButton
            className={classnames(classes.baseButton, classes.secondaryButton, classes.smallIconButton)}
            onClick={listActionsMenuClick}
          >
            <MoreHoriz sx={{ fontSize: '20px' }} />
          </IconButton>
        )}
      </Box>

      {formattedObjects.length ? (
        <div>
          <Box
            component={listContainerComponent}
            elevation={listContainerElevation}
            className={listContainerStyle || classes.listContainer}
          >
            <TableContainer ref={tableRef}>
              <Table className={classes.table}>
                {renderHeader && (
                  <TableHead>
                    <TableRow className={classes.tableHeader}>{renderHeaderCells()}</TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {formattedObjects.map((object) => {
                    const key = objectKey ? object[objectKey] : object._id;
                    const rowDisabled = disabled(object);
                    const selected = rowSelected(object);
                    const archived = rowArchived(object);
                    return (
                      <TableRow
                        key={key}
                        title={rowDisabled ? disabledTitle : null}
                        className={classnames(
                          classes.tableRow,
                          { [classes.tableRowDisabled]: rowDisabled },
                          { [classes.tableRowArchived]: archived },
                          { selected }
                        )}
                        onClick={getTableRowClick(object, rowDisabled)}
                      >
                        {renderCells(object)}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box className={paginationStyle || classes.pagination}>
            {!loading && paginationLinks && (
              <PaginationComponent links={paginationLinks} totalDocs={totalDocs} handleChange={handleChange} />
            )}
          </Box>
        </div>
      ) : (
        <TlEmptyResults
          messageType={autoCompleteTerms ? 'searching' : 'following'}
          buttonFunction={
            autoCompleteTerms
              ? () => {
                  setAutoCompleteTerms('');
                }
              : () => {
                  setFollowingFiltered(false);
                }
          }
        />
      )}

      <Menu
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => {
          applyFilters();
          setFilterAnchor(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          style: {
            minWidth: '170px',
            borderRadius: '10px',
          },
        }}
      >
        <div className={classes.filterOptions}>
          <div className={classes.filterTitle}>Source Type</div>
          <FormGroup>
            {Object.keys(sourceTypeLabels).map((sourceType) => {
              const checked = filterTypes[sourceType] !== undefined ? filterTypes[sourceType] : false;
              return (
                <FormControlLabel
                  label={<div className={classes.filterOptionLabel}>{sourceTypeLabels[sourceType]}</div>}
                  key={sourceType}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={(e) => handleFilterChange(e, sourceType)}
                      name={sourceTypeLabels[sourceType]}
                      style={{
                        color: checked ? studioPrimaryColor : colorVariables.grey500,
                      }}
                    />
                  }
                />
              );
            })}
          </FormGroup>
          <Button
            onClick={() => {
              applyFilters();
              setFilterAnchor(null);
            }}
            className={classnames(classes.filterButton, classes.baseButton, classes.keyButton)}
          >
            Filter
          </Button>
        </div>
      </Menu>

      <Menu
        open={Boolean(adServerFilterAnchor)}
        anchorEl={adServerFilterAnchor}
        onClose={() => {
          applyFilters();
          setAdServerFilterAnchor(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          style: {
            minWidth: '170px',
            borderRadius: '10px',
          },
        }}
      >
        <div className={classes.filterOptions}>
          <div className={classes.filterTitle}>Ad Server</div>
          <FormGroup>
            {adServerOptions.map(({ key, displayName }) => {
              const checked = adServerOptionFilters[key] !== undefined ? adServerOptionFilters[key] : false;

              return (
                key && (
                  <FormControlLabel
                    label={<div className={classes.filterOptionLabel}>{displayName}</div>}
                    key={key}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => handleAdServerFilterChange(e, key)}
                        name={displayName}
                        style={{
                          color: checked ? studioPrimaryColor : colorVariables.grey500,
                        }}
                      />
                    }
                  />
                )
              );
            })}
          </FormGroup>
          <Button
            onClick={() => {
              applyFilters();
              setAdServerFilterAnchor(null);
            }}
            className={classnames(classes.filterButton, classes.baseButton, classes.keyButton)}
          >
            Filter
          </Button>
        </div>
      </Menu>
    </div>
  );
};

TlFilterList.defaultProps = {
  columns: { title: 'name' },
  rowClick: undefined,
  title: null,
  objectKey: null,
  disabled: () => false,
  disabledTitle: 'Option disabled',
  rowSelected: () => false,
  rowArchived: () => false,
  followingFilterType: '',
  searchArgs: {},
  searchDocs: nonAdminSearchDocs,
  serviceArgs: [],
  showArchivedFilter: false,
  showSourceTypeFilter: false,
  showAdServerFilter: false,
  showSearchBar: true,
  objectSingularLabel: '',
  actionButtonUrl: '',
  secondaryActionButtonUrl: '',
  secondaryActionButtonHandler: undefined,
  secondaryActionButtonLabel: '',
  secondaryActionButtonIcon: null,
  triggerRefresh: false,
  emptyStateType: '',
  emptyStateButtonFunction: null,
  emptyStateLink: null,
  accountId: undefined,
  listActionsMenuClick: null,
  objectFormatter: () => {},
  emptyStateButtonText: null,
  renderHeader: true,
  topControlsWrapperStyle: null,
  listContainerComponent: Box,
  listContainerElevation: 0,
  listContainerStyle: null,
  paginationComponent: paginationComponents.tlPagnation,
  paginationStyle: null,
};

TlFilterList.propTypes = {
  title: PropTypes.string,
  objectKey: PropTypes.string,
  linkTo: PropTypes.func.isRequired,
  columns: PropTypes.shape({}),
  rowClick: PropTypes.func,
  disabled: PropTypes.func,
  disabledTitle: PropTypes.string,
  rowSelected: PropTypes.func,
  rowArchived: PropTypes.func,
  followingFilterType: PropTypes.string,
  fetchObjectsByIds: PropTypes.func.isRequired,
  fetchObjects: PropTypes.func.isRequired,
  searchArgs: PropTypes.shape({}),
  searchDocs: PropTypes.func,
  serviceArgs: PropTypes.arrayOf(PropTypes.string),
  searchType: PropTypes.string.isRequired,
  objectPluralLabel: PropTypes.string.isRequired,
  objectSingularLabel: PropTypes.string,
  actionButtonUrl: PropTypes.string,
  secondaryActionButtonUrl: PropTypes.string,
  secondaryActionButtonHandler: PropTypes.func,
  secondaryActionButtonLabel: PropTypes.string,
  secondaryActionButtonIcon: PropTypes.node,
  listActionsMenuClick: PropTypes.func,
  showArchivedFilter: PropTypes.bool,
  showSourceTypeFilter: PropTypes.bool,
  showAdServerFilter: PropTypes.bool,
  triggerRefresh: PropTypes.bool,
  emptyStateType: PropTypes.string,
  emptyStateButtonFunction: PropTypes.func,
  emptyStateLink: PropTypes.func,
  showSearchBar: PropTypes.bool,
  accountId: PropTypes.string,
  objectFormatter: PropTypes.func,
  emptyStateButtonText: PropTypes.string,
  renderHeader: PropTypes.bool,
  topControlsWrapperStyle: PropTypes.string,
  listContainerComponent: PropTypes.shape({}),
  listContainerElevation: PropTypes.number,
  listContainerStyle: PropTypes.string,
  paginationComponent: PropTypes.func,
  paginationStyle: PropTypes.string,
};

export default TlFilterList;
